import {
  Box,
  BoxProps,
  Icon,
  IconButton,
  Stack,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import { useSendInviteMutation } from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { DELAY_BEFORE_HIDE_ICON_DONE, SchoolInviteStatus } from '@schooly/constants';
import { CopyIcon, DoneIcon, MailIcon, SimpleButton, Spin } from '@schooly/style';
import { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

export type ContactDetailsTooltipProps = {
  name?: string;
  email?: string;
  telephone?: string;
  contactName?: string;
  children: TooltipProps['children'];
  inviteStatus?: SchoolInviteStatus | null;
  schoolId: string;
  relationId: string;
} & BoxProps;

export const ContactDetailsTooltip: FC<ContactDetailsTooltipProps> = ({
  name,
  email,
  telephone,
  contactName,
  children,
  inviteStatus,
  schoolId,
  relationId,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const sendInvite = useSendInviteMutation();
  const hasContactDetails = email || telephone || contactName;

  const { showError } = useNotifications();
  const { isLoading } = sendInvite;

  const userInvited = inviteStatus === SchoolInviteStatus.Invited;
  const inviteSent = sendInvite.isSuccess;

  const sendInviteHandler = useCallback(() => {
    sendInvite.mutate({ schoolId, relationId }, { onError: showError });
  }, [relationId, schoolId, sendInvite, showError]);

  const inviteButtonTextId = () => {
    if (isLoading) return 'sending-invite';
    if (inviteSent) return 'inviteStatus-Invited';
    if (userInvited) return 'resend-invite';
    return 'send-invite';
  };

  const inviteButtonIcon = () => {
    if (isLoading) return <Spin />;
    if (inviteSent) return <DoneIcon />;
    return <MailIcon />;
  };

  return (
    <Box onClick={(e) => e.stopPropagation()} {...props}>
      <Tooltip
        componentsProps={{ tooltip: { sx: { minWidth: 200, maxWidth: 300, padding: 1.25 } } }}
        title={
          name && (
            <Stack gap={1}>
              <Typography color="text.primary" textAlign="center">
                {name}
              </Typography>
              {hasContactDetails && (
                <Stack gap={0.25}>
                  {contactName && (
                    <ContactRow
                      contact={contactName}
                      contactLabelId="companies-ContactPersonName"
                    />
                  )}
                  {telephone && (
                    <ContactRow contact={telephone} contactLabelId="peopleDetail-PhoneNumber" />
                  )}
                  {email && (
                    <ContactRow contact={email} contactLabelId="peopleDetail-EmailAddress" />
                  )}
                </Stack>
              )}
              {inviteStatus !== undefined && inviteStatus !== SchoolInviteStatus.Active && (
                <Stack alignItems="flex-start" gap={1}>
                  <Typography>
                    {formatMessage(
                      {
                        id: userInvited
                          ? 'profile-HasNotAcceptTheInvitation'
                          : 'profile-NotInvitedToSchooly',
                      },
                      { name },
                    )}
                  </Typography>
                  {email && (
                    <SimpleButton
                      size="small"
                      sx={{ '&.Mui-disabled': { color: 'primary.main' } }}
                      disabled={isLoading || inviteSent}
                      startIcon={inviteButtonIcon()}
                      onClick={sendInviteHandler}
                    >
                      {formatMessage({ id: inviteButtonTextId() })}
                    </SimpleButton>
                  )}
                </Stack>
              )}
            </Stack>
          )
        }
      >
        {children}
      </Tooltip>
    </Box>
  );
};

type ContactRowProps = {
  contact: string;
  contactLabelId: string;
};

const ContactRow: FC<ContactRowProps> = ({ contact, contactLabelId }) => {
  const { showNotification } = useNotifications();
  const { formatMessage } = useIntl();
  const [showDoneIcon, setShowDoneIcon] = useState(false);

  const onCopy = () => {
    navigator.clipboard.writeText(contact);

    setShowDoneIcon(true);

    setTimeout(() => setShowDoneIcon(false), DELAY_BEFORE_HIDE_ICON_DONE);

    showNotification({
      textId: 'profile-SuccessfullyCopied',
      type: 'success',
      values: { contactDetail: formatMessage({ id: contactLabelId }) },
    });
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      p={0.25}
      pl={0.5}
      sx={(theme) => ({
        borderRadius: theme.spacing(0.5),
        '.MuiIconButton-root': { visibility: 'hidden' },
        ':hover': { bgcolor: 'common.lightBg', '.MuiIconButton-root': { visibility: 'visible' } },
      })}
    >
      <Typography variant="h3" noWrap sx={{ textOverflow: 'ellipsis', maxWidth: '100%' }}>
        {contact}
      </Typography>
      {showDoneIcon ? (
        <Icon sx={{ color: 'primary.main' }}>
          <DoneIcon />
        </Icon>
      ) : (
        <IconButton onClick={onCopy}>
          <CopyIcon />
        </IconButton>
      )}
    </Stack>
  );
};
